<div class="collections-container">

  @if (rows().length > 0) {
    <div class="collections-list"
         [sortableArray]="rows()" [sortableOptions]="sortableOptions" (sortableChange)="onSort()">
      @for (row of rows(); track row; let i = $index) {
        <div class="collection-row">
          @if (isEditing()) {
            <nb-icon pack="eva" icon="more-vertical-outline" class="drag-handle"></nb-icon>
          }
          <div class="row-accent" [style.background-color]="row.collection.color || '#cccccc'"></div>

          <div class="row-main">
            <div class="row-header">
              <div class="row-title-group">
                <span class="collection-title">{{ row.collection.title }}</span>
                @if (row.required && !isEditing()) {
                  <span class="pflicht-indicator">
                    <nb-icon icon="alert-circle-outline" pack="eva"></nb-icon>
                    Pflicht
                  </span>
                }
              </div>
              @if (isEditing()) {
                <div class="row-actions">
                  <button nbButton ghost size="small"
                          [status]="row.required ? 'warning' : 'basic'"
                          [nbTooltip]="row.required ? 'Pflichtabschnitt – klicken zum Aufheben' : 'Als Pflichtabschnitt markieren'"
                          (click)="toggleRequired(i, !row.required)">
                    <nb-icon [icon]="row.required ? 'alert-circle' : 'alert-circle-outline'" pack="eva"></nb-icon>
                  </button>
                  <button nbButton ghost size="small" status="danger" (click)="removeRow(i)" nbTooltip="Entfernen">
                    <nb-icon icon="close-outline" pack="eva"></nb-icon>
                  </button>
                </div>
              }
            </div>

            @if (row.collectionDetail?.members?.length) {
              <div class="field-list">
                @for (member of row.collectionDetail!.members; track member.id) {
                  <span class="field-chip"
                        [class.required]="member.required"
                        nbTooltip="Pflichtfeld"
                        [nbTooltipDisabled]="!member.required"
                        nbTooltipStatus="warning">
                    @if (member.required) {
                      <nb-icon icon="alert-circle-outline" pack="eva" class="req-icon"></nb-icon>
                    }
                    @if (member.visibleIfFieldId) {
                      <nb-icon icon="eye-outline" pack="eva" class="vis-icon"
                               nbTooltip="Wird nur unter einer Bedingung angezeigt"
                               nbTooltipStatus="info"></nb-icon>
                    }
                    {{ member.field.name }}
                  </span>
                }
              </div>
            }

            @if (!row.collectionDetail) {
              <div class="field-list-loading">
                <nb-spinner size="tiny"></nb-spinner>
              </div>
            }

            @if (row.collectionDetail && !row.collectionDetail.members.length) {
              <span class="no-fields">Keine Felder konfiguriert</span>
            }
          </div>
        </div>
      }
    </div>
  } @else {
    <p class="empty-hint">Noch keine Feldgruppen zugewiesen.</p>
  }

  @if (isEditing()) {
    <div class="add-row">
      <button
        nbButton
        ghost
        size="small"
        type="button"
        [nbPopover]="collectionPickerTemplate"
        [nbPopoverPlacement]="NbPosition.BOTTOM_START"
        [nbPopoverAdjustment]="NbAdjustment.COUNTERCLOCKWISE"
        #collectionPicker="nbPopover">
        <nb-icon icon="plus-outline" pack="eva"></nb-icon>
        Feldgruppe hinzufügen
      </button>
    </div>
  }

</div>

<ng-template #collectionPickerTemplate>
  <div class="collection-picker">

    <!-- Search -->
    <div class="picker-search">
      <nb-form-field>
        <nb-icon nbPrefix icon="search-outline" pack="eva"></nb-icon>
        <input
          nbInput
          fullWidth
          size="small"
          placeholder="Suchen..."
          [value]="searchText$.value"
          (input)="onSearchInput($event)"
          autocomplete="off">
      </nb-form-field>
    </div>

    <!-- Back button — stopPropagation prevents Nebular from reading the click
         target after it's been removed from the DOM by the block below. -->
    @if (currentFolder$ | async; as currentFolder) {
      <div class="picker-breadcrumb">
        <button nbButton ghost size="tiny" type="button"
                (click)="navigateToParent($event)">
          <nb-icon icon="arrow-back-outline" pack="eva"></nb-icon>
          {{ folderDisplayName(currentFolder) }}
        </button>
      </div>
    }

    <!-- Subfolders — only when not searching -->
    @if (((searchText$ | async) ?? '') === '') {
      @if (foldersWithCount$ | async; as folders) {
        @if (folders.length) {
          <div class="picker-folders">
            @for (folder of folders; track folder.fullPath) {
              <div
                class="picker-folder-item"
                role="button"
                tabindex="0"
                (click)="navigateToFolder($event, folder.fullPath)"
                (keydown.enter)="navigateToFolder($event, folder.fullPath)"
                (keydown.space)="navigateToFolder($event, folder.fullPath); $event.preventDefault()">
                <nb-icon icon="folder-outline" pack="eva"></nb-icon>
                <span class="folder-name">{{ folder.name }}</span>
                <span class="folder-count">{{ folder.count }}</span>
                <nb-icon icon="chevron-right-outline" pack="eva" class="folder-arrow"></nb-icon>
              </div>
            }
          </div>
        }
      }
    }

    <!-- Collections list -->
    <div class="picker-collections">
      @if (filteredCollections$ | async; as collections) {

        @if (!collections.length) {
          <div class="picker-empty">
            <nb-icon icon="inbox-outline" pack="eva"></nb-icon>
            <span>Keine Feldgruppen gefunden</span>
          </div>
        }

        @for (collection of collections; track collection.id) {
          <div
            class="picker-collection-item"
            role="button"
            tabindex="0"
            (click)="selectCollection(collection)"
            (keydown.enter)="selectCollection(collection)"
            (keydown.space)="selectCollection(collection); $event.preventDefault()">

            <div class="picker-accent" [style.background-color]="collection.color || '#cccccc'"></div>

            <div class="picker-collection-body">
              <div class="picker-collection-header">
                <span class="picker-collection-title">{{ collection.title }}</span>
                @if ((searchText$ | async) && collection.category) {
                  <span class="picker-collection-category">
                    {{ categoryLabel(collection.category) }}
                  </span>
                }
              </div>

              @if (collection.members.length) {
                <div class="picker-field-list">
                  @for (member of collection.members; track member.id) {
                    <span
                      class="picker-field-chip"
                      [class.required]="member.required">
                      @if (member.required) {
                        <nb-icon
                          icon="alert-circle-outline"
                          pack="eva"
                          class="req-icon"
                          nbTooltip="Pflichtfeld"
                          nbTooltipStatus="warning">
                        </nb-icon>
                      }
                      @if (member.visibleIfFieldId) {
                        <nb-icon
                          icon="eye-outline"
                          pack="eva"
                          class="vis-icon"
                          nbTooltip="Wird nur unter einer Bedingung angezeigt"
                          nbTooltipStatus="info">
                        </nb-icon>
                      }
                      <span class="field-name">{{ member.field.name }}</span>
                      <span class="field-type">{{ displayFieldType(member.field.composition.valueType) }}</span>
                      @if (member.field.composition.unit) {
                        <span class="field-unit">
                          {{ displayUnit(member.field.composition.unit) }}
                        </span>
                      }
                    </span>
                  }
                </div>
              }

              @if (!collection.members.length) {
                <span class="picker-no-fields">Keine Felder konfiguriert</span>
              }
            </div>

          </div>
        }

      }
    </div>

  </div>
</ng-template>
